import * as Yup from 'yup'
import fields from './definitions/formFields'
import type { Field, FieldName } from './types'

export const inputRegEx: Record<string, RegExp> = {
  number: /^[-+]?[0-9]*\.?[0-9]+(?:\/[0-9]*\.?[0-9]+)?$/,
  integer: /^\d+$/,
  phone: /^(\+\d{1,3})?\s?\(?\d{1,4}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
  email: /^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/,
}

export type ValidationFunctionName = keyof typeof validationFunctions
const validationFunctions = {
  number: () =>
    Yup.string().matches(inputRegEx.number, 'Input must be a number.'),

  integer: () =>
    Yup.string().matches(inputRegEx.integer, 'Input must be an integer.'),

  integerArray: () =>
    Yup.array().of(
      Yup.string().matches(inputRegEx.integer, 'Input must be integers.'),
    ),

  phone: () =>
    Yup.string().matches(inputRegEx.phone, 'Phone number must match format.'),

  email: () => Yup.string().matches(inputRegEx.email, 'Not a valid email.'),

  minimum: (min: number) => Yup.array().min(min, `Requires ${min} selection.`),

  maximum: (max: number) =>
    Yup.array().max(max, `Cannot exceed ${max} selections.`),

  maxChars: (max: number) =>
    Yup.string().max(max, `Cannot exceed ${max} characters.`),

  maxCharArray: (max: number) =>
    Yup.array().of(
      Yup.string().max(max, `Cannot exceed ${max} characters per item.`),
    ),

  length: (length: number) =>
    Yup.string().length(
      length,
      `Cannot exceed ${length.toLocaleString()} characters`,
    ),

  required: () => Yup.mixed().required('A selection is required'),
}

function generateFieldSchemas(): Partial<Record<FieldName, Yup.Schema[]>> {
  const fieldValidations: Partial<Record<FieldName, Yup.Schema[]>> = {}
  for (const fieldName in fields) {
    const field = fields[fieldName as FieldName] as Field
    const validations = (fieldValidations[fieldName as FieldName] =
      [] as Yup.Schema[])

    if (!field || !field.validations || !Array.isArray(field.validations))
      continue

    for (const fieldValidation of field.validations) {
      const func =
        validationFunctions[fieldValidation.type as ValidationFunctionName]
      const params = fieldValidation?.params || []
      const setFunc = (func as (...args: number[]) => Yup.Schema)(...params)
      validations.push(setFunc)
    }
  }
  return fieldValidations
}

export default generateFieldSchemas()
